/**
 * Portal Configuration
 * 
 * Principle 16: One Platform, Multiple Portals
 * 
 * Leynix is one platform providing personalized operational experiences.
 * Every authenticated user experiences Leynix differently based on their role.
 * 
 * Portal Mapping:
 * - platform_owner, agency_admin → Executive Portal
 * - hr_staff, hr_compliance → HR/Compliance Portal
 * - operations, client_caregiver_services → Operations Portal
 * - payroll, finance → Finance Portal
 * - caregiver, employee → My Leynix
 * - scheduler → Scheduler Portal
 * - supervisor → Supervisor Portal
 * - applicant → Applicant Portal
 */

// Portal definitions
export const PORTALS = {
  EXECUTIVE: {
    id: 'executive',
    name: 'Executive Portal',
    description: 'Agency oversight, revenue, reports',
    path: '/portal/executive',
    icon: 'Building2',
    color: 'bg-blue-600',
    roles: ['platform_owner', 'agency_admin'],
  },
  HR_COMPLIANCE: {
    id: 'hr_compliance',
    name: 'HR & Compliance',
    description: 'Hiring, employees, compliance, training',
    path: '/portal/hr',
    icon: 'Users',
    color: 'bg-purple-600',
    roles: ['hr_staff', 'hr_compliance', 'hr_manager'],
  },
  OPERATIONS: {
    id: 'operations',
    name: 'Operations',
    description: 'Clients, caregivers, scheduling',
    path: '/portal/operations',
    icon: 'Briefcase',
    color: 'bg-green-600',
    roles: ['operations', 'client_caregiver_services', 'operations_manager'],
  },
  FINANCE: {
    id: 'finance',
    name: 'Finance',
    description: 'Payroll, billing, invoices',
    path: '/portal/finance',
    icon: 'DollarSign',
    color: 'bg-emerald-600',
    roles: ['payroll', 'finance', 'billing', 'finance_manager'],
  },
  MY_ALIGNED: {
    id: 'my_aligned',
    name: 'My Leynix',
    description: 'Your personal workspace',
    path: '/my-aligned',
    icon: 'Star',
    color: 'bg-[#176DFF]',
    roles: ['caregiver', 'employee'],
  },
  SCHEDULER: {
    id: 'scheduler',
    name: 'Scheduler',
    description: 'Scheduling and coverage',
    path: '/portal/scheduler',
    icon: 'Calendar',
    color: 'bg-cyan-600',
    roles: ['scheduler'],
  },
  SUPERVISOR: {
    id: 'supervisor',
    name: 'Supervisor',
    description: 'Field oversight and coaching',
    path: '/portal/supervisor',
    icon: 'Eye',
    color: 'bg-orange-600',
    roles: ['supervisor', 'field_supervisor'],
  },
  APPLICANT: {
    id: 'applicant',
    name: 'Applicant Portal',
    description: 'Application and onboarding',
    path: '/portal/applicant',
    icon: 'FileText',
    color: 'bg-slate-600',
    roles: ['applicant'],
  },
};

// Portal priority order (higher index = higher privilege)
const PORTAL_PRIORITY = [
  'applicant',
  'my_aligned',
  'supervisor',
  'scheduler',
  'operations',
  'hr_compliance',
  'finance',
  'executive',
];

/**
 * Get the primary portal for a user based on their role
 */
export function getPrimaryPortal(userRole) {
  if (!userRole) return PORTALS.MY_ALIGNED;
  
  const role = userRole.toLowerCase();
  
  // Find matching portal
  for (const portal of Object.values(PORTALS)) {
    if (portal.roles.includes(role)) {
      return portal;
    }
  }
  
  // Default to My Leynix for unknown roles
  return PORTALS.MY_ALIGNED;
}

/**
 * Get all portals a user can access based on their role(s)
 */
export function getAccessiblePortals(userRole, additionalRoles = []) {
  const roles = [userRole, ...additionalRoles].map(r => r?.toLowerCase()).filter(Boolean);
  
  const accessible = [];
  
  for (const portal of Object.values(PORTALS)) {
    if (portal.roles.some(r => roles.includes(r))) {
      accessible.push(portal);
    }
  }
  
  // Sort by priority (highest first for portal switcher)
  accessible.sort((a, b) => {
    return PORTAL_PRIORITY.indexOf(b.id) - PORTAL_PRIORITY.indexOf(a.id);
  });
  
  return accessible;
}

/**
 * Check if user can access a specific portal
 */
export function canAccessPortal(userRole, portalId) {
  const portal = Object.values(PORTALS).find(p => p.id === portalId);
  if (!portal) return false;
  
  return portal.roles.includes(userRole?.toLowerCase());
}

/**
 * Get portal by ID
 */
export function getPortalById(portalId) {
  return Object.values(PORTALS).find(p => p.id === portalId);
}

/**
 * Check if user should see portal switcher
 * (Only if they have access to more than one portal)
 */
export function shouldShowPortalSwitcher(userRole, additionalRoles = []) {
  const accessible = getAccessiblePortals(userRole, additionalRoles);
  return accessible.length > 1;
}

export default PORTALS;
